"use client";

import React, { Suspense, createContext, useCallback, useContext, useEffect, useState } from "react";
import LightBox from "@/components/light-box/LightBox";
import { Attribute } from "@/types/attribute";
import { fetchGlobal } from "@/services/global";
import { fetchAllAttributes } from "@/services/attributes";
import { useAuth } from "@/lib/auth-context";
import { useRouter, useSearchParams } from "next/navigation";

type GlobalData = Awaited<ReturnType<typeof fetchGlobal>>;

interface LightBoxContextValue {
  /** Open the site's single "Plant For A Cause" lightbox, optionally with a cause preselected. */
  openLightBox: (attribute?: Attribute | null) => void;
}

const LightBoxContext = createContext<LightBoxContextValue>({ openLightBox: () => {} });

export const useLightBox = () => useContext(LightBoxContext);

// Derive a URL-friendly slug from an attribute name (e.g. "Climate Healing" -> climate-healing).
const attributeSlug = (name: string) =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Opens the site-wide lightbox whenever the current URL carries ?attribute=<slug>,
 * on ANY page (not just the homepage) — so the lightbox opens over the page you're
 * already on, instead of forcing a trip to the homepage.
 */
function AttributeQueryOpener({
  attributes,
  openLightBox,
}: {
  attributes: Attribute[];
  openLightBox: (attr: Attribute | null) => void;
}) {
  const searchParams = useSearchParams();
  useEffect(() => {
    const cause = searchParams.get("attribute");
    if (!cause || attributes.length === 0) return;
    const match = attributes.find(
      (a) => attributeSlug(a.name) === cause.toLowerCase()
    );
    openLightBox(match ?? null);
  }, [searchParams, attributes, openLightBox]);
  return null;
}

/** One lightbox for the whole site; Header, mobile menu and cause cards all open this one. */
export function LightBoxProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [attribute, setAttribute] = useState<Attribute | null>(null);
  // While resuming after login, don't pass a preselected cause: it would overwrite the restored one.
  const [resuming, setResuming] = useState(false);
  const [globalData, setGlobalData] = useState<GlobalData | null>(null);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const { isAuthenticated } = useAuth();
  const router = useRouter();

  useEffect(() => {
    fetchGlobal().then((data) => data && setGlobalData(data));
    fetchAllAttributes().then((list) => list && setAttributes(list));
  }, []);

  // Back from HFN login while mid-payment: reopen with the saved state.
  useEffect(() => {
    if (!isAuthenticated || typeof window === "undefined") return;
    if (localStorage.getItem("fbh_pending_lightbox") === "true") {
      localStorage.removeItem("fbh_pending_lightbox");
      setAttribute(null);
      setResuming(true);
      setIsOpen(true);
    }
  }, [isAuthenticated]);

  const openLightBox = useCallback((attr: Attribute | null = null) => {
    setResuming(false);
    setAttribute(attr);
    setIsOpen(true);
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setResuming(false);
      // Clear ?attribute on close so clicking the same CTA link re-opens the
      // lightbox (the opener effect only fires when the query actually changes).
      if (typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        if (params.has("attribute")) {
          params.delete("attribute");
          const qs = params.toString();
          const base = window.location.pathname; // keeps the trailing slash the static export serves
          router.replace(qs ? `${base}?${qs}` : base, { scroll: false });
        }
      }
    }
  }, [router]);

  return (
    <LightBoxContext.Provider value={{ openLightBox }}>
      {children}
      {/* The lightbox reads URL search params; static export requires a Suspense boundary. */}
      <Suspense fallback={null}>
      <AttributeQueryOpener attributes={attributes} openLightBox={openLightBox} />
      <LightBox
        hideTrigger
        attributes={attributes}
        preSelectedAttribute={resuming ? null : attribute ?? globalData?.default_attribute ?? null}
        co2Sequestration={globalData?.co2_sequestation}
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
      />
      </Suspense>
    </LightBoxContext.Provider>
  );
}
